import {
  makeObservable,
  observable,
  action,
  runInAction,
  computed,
} from 'mobx';
import {
  getTriggers,
  getUser,
  signInV2,
  checkIsUserPaying,
  createCustomerPortalSession,
  updateUserCookieConsent,
} from '../audiblogsApi';
import mixpanel from 'mixpanel-browser';
import firebase from '../firebase';
import { boot, update } from "@intercom/messenger-js-sdk";
import { isMobile } from 'react-device-detect';
import gamificationStore from './GamificationStore';

const getIdToken = () => firebase.auth().currentUser.getIdToken(true);

class UserStore {
  user = null;
  firebaseUser = null;
  userFeedUrl = null;
  creditsHd = null;
  creditsSd = null;
  isUserPaying = null;
  voiceQuality = null;
  customerPortalUrl = '';
  idToken = '';
  trackingId = '';
  intervalId = 0;
  initialized = false;
  triggers = [];
  vipId = null;
  additionalCharacters = 0;
  hideChallenges = false;
  privacyMode = false;
  cookieConsent = null;

  constructor() {
    makeObservable(this, {
      initialized: observable,
      user: observable,
      firebaseUser: observable,
      creditsHd: observable,
      isUserPaying: observable,
      voiceQuality: observable,
      creditsSd: observable,
      userFeedUrl: observable,
      idToken: observable,
      customerPortalUrl: observable,
      triggers: observable.shallow,
      trackingId: observable,
      intervalId: observable,
      isSignedIn: computed,
      vipId: observable,
      additionalCharacters: observable,
      hideChallenges: observable,
      privacyMode: observable,
      cookieConsent: observable,
      updateTrackingId: action.bound,
      updateUser: action.bound,
      logOut: action.bound,
      updateVipId: action.bound,
      refreshIdToken: action.bound,
      updateAdditionalCharacters: action.bound,
      updateHideChallenges: action.bound,
      updatePrivacyMode: action.bound,
      updateCookieConsent: action.bound,
    });

    // Set up token refresh every 30 minutes (token expires after 1 hour)
    this.intervalId = setInterval(this.refreshIdToken, 30 * 60 * 1000);

    firebase.auth().onAuthStateChanged((firebaseUser) => {
      runInAction(() => {
        this.firebaseUser = firebaseUser;
      });
      
      if (firebaseUser && firebaseUser.email) {
        let authToken;
        getIdToken()
          .then((idToken) => {
            authToken = idToken;
            let mixpanel_distinct_id = '';
            try {
              mixpanel_distinct_id = mixpanel.get_distinct_id();
            } catch (e) {
              console.log(e);
            }

            update({
              email: firebaseUser.email,
              name: firebaseUser.displayName,
              hide_default_launcher: isMobile,
            });

            return signInV2(idToken, mixpanel_distinct_id, this.vipId);
          })
          .then((res) => {
            runInAction(() => {
              this.user = res.data;
              if (firebaseUser.displayName) {
                this.user.displayName = firebaseUser.displayName;
              }
              this.userFeedUrl = `https://audiblogs-conversions-new.s3.us-east-1.amazonaws.com/rss/${res.data.user_id}.xml`;
              this.creditsHd = res.data.credits_hd;
              this.creditsSd = res.data.credits_sd;
              this.cookieConsent = res.data.cookieConsent || null;
              this.initialized = true;
              this.idToken = authToken;
              
              // Load hideChallenges preference from localStorage
              const savedHideChallenges = localStorage.getItem('hideChallenges');
              if (savedHideChallenges !== null) {
                this.hideChallenges = JSON.parse(savedHideChallenges);
              }
              
              // Initialize gamification store
              gamificationStore.initialize();

              // Ensure we identify with the DynamoDB UUID
              if (res.data.user_id) {
                mixpanel.identify(res.data.user_id);
                // Get stored UTM params from localStorage
                const storedUtmParams = JSON.parse(localStorage.getItem('utm_params') || '{}');
                mixpanel.people.set({
                  $email: firebaseUser.email,
                  $name: firebaseUser.displayName,
                  ...storedUtmParams
                });
              }
            });

            return Promise.all([
              checkIsUserPaying(this.idToken),
              createCustomerPortalSession(this.idToken)
            ]);
          })
          .then(([payingRes, portalRes]) => {
            runInAction(() => {
              this.isUserPaying = payingRes.data.is_paying_user;
              this.voiceQuality = payingRes.data.voice_quality;
              try {
                this.customerPortalUrl = portalRes.url || '';
              } catch (e) {
                console.log(e);
                this.customerPortalUrl = '';
              }
            });
          })
          .catch(error => {
            console.error('Error during auth flow:', error);
            // Handle network errors gracefully
            runInAction(() => {
              this.initialized = true;
              // Don't clear other state on network error
            });
          });
      } else {
        runInAction(() => {
          if (this.intervalId) {
            window.clearInterval(this.intervalId);
          }
          this.initialized = true;
          this.user = null;
          this.idToken = '';
          this.intervalId = 0;
          this.triggers = [];
        });
      }
    });
  }

  get isSignedIn() {
    return this.initialized ? !!this.idToken : null;
  }

  updateTrackingId(newTrackingId) {
    this.trackingId = newTrackingId;
  }

  updateUser() {
    getUser(this.idToken).then((res) => {
      runInAction(() => {
        this.user = res.data;
        this.creditsHd = res.data.credits_hd;
        this.creditsSd = res.data.credits_sd;
        // Set privacy mode from user data
        this.privacyMode = res.data.privacyMode || false;
        // Set cookie consent from user data
        this.cookieConsent = res.data.cookieConsent || null;
      });
    });
    checkIsUserPaying(this.idToken).then((res) => {
      this.isUserPaying = res.data.is_paying_user;
      this.voiceQuality = res.data.voice_quality;
    });
  }

  updateVipId(vipId) {
    this.vipId = vipId;
  }

  updateAdditionalCharacters(characters) {
    this.additionalCharacters = characters;
  }

  updateHideChallenges(hideChallenges) {
    this.hideChallenges = hideChallenges;
  }

  updatePrivacyMode(privacyMode) {
    this.privacyMode = privacyMode;
  }

  updateCookieConsent(consent) {
    this.cookieConsent = consent;
  }

  refreshIdToken = async () => {
    if (this.firebaseUser) {
      try {
        const newToken = await this.firebaseUser.getIdToken(true); // Force refresh
        runInAction(() => {
          this.idToken = newToken;
        });
      } catch (error) {
        console.error('Error refreshing token:', error);
        // If we can't refresh the token, log the user out
        if (error.code === 'auth/requires-recent-login') {
          this.logOut();
        }
      }
    }
  };

  async logOut() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = 0;
    }
    await firebase.auth().signOut();

    // Clear MobX user state
    this.user = null;
    this.idToken = null;
    this.subscriptions = null;
    this.cookieConsent = null;
    // ...clear any other relevant fields...

    // Clear localStorage and sessionStorage
    localStorage.clear();
    sessionStorage.clear();

    // Clear cookies
    document.cookie.split(';').forEach(c => {
      document.cookie = c
        .replace(/^ +/, '')
        .replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/');
    });

    // Hard redirect to login page
    window.location.href = '/signin?source=logout';
  }
}

const userStore = new UserStore();

export default userStore;
